import { Badge, Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { styledClient } from '@hero/clients/styledClient';
import { DEVICE, SIDEBAR_WIDTH } from '@hero/constants';

const SidebarHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 0 4px;
  position: relative;
`;

const SidebarHeaderButton = styled.button`
  align-items: center;
  background-color: var(--color-components-sidenav-icon-bg);
  border: 1px solid var(--color-border-default);
  border-radius: 50%;
  color: var(--color-components-sidenav-icon-fg);
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: center;
  outline: none;
  position: absolute;
  right: -32px;
  top: 32px;
  transition: all 0.2s ease-in-out;
  width: 24px;

  &:hover {
    transform: scale(1.1);
  }
`;

const SidebarButton = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: var(--size-border-radius-md);
  color: var(--color-components-sidenav-new-fg);
  cursor: pointer;
  display: flex;
  gap: 6px;
  height: 40px;
  margin: 24px 0 12px;
  outline: none;
  padding: 0;
  transition: all 0.2s ease-in-out;

  ${Icon.S.Icon} svg {
    height: 28px;
    width: 28px;
  }

  &:hover {
    background-color: var(--color-components-sidenav-menu-item-bg-hover);
    border-radius: var(--size-border-radius-md);
    color: var(--color-components-sidenav-new-fg);
  }

  &.active {
    background-color: var(--color-components-sidenav-menu-item-bg-active);
    color: var(--color-components-sidenav-menu-item-fg-active);
    font-weight: 700;
  }
`;

const SidebarButtonIcon = styled.div`
  align-items: center;
  background-color: var(--color-components-sidenav-new-bg);
  border: 1px solid var(--color-components-sidenav-new-border);
  border-radius: 50%;
  color: var(--color-components-sidenav-new-fg-inverse);
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 4px;
  outline: none;
  width: 32px;

  &:hover {
    background-color: var(--color-components-sidenav-new-bg-hover);
    border: 1px solid var(--color-components-sidenav-new-border-hover);
  }
`;

const SidebarHeaderLogo = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const SidebarHeaderLogoText = styled.div`
  overflow: hidden;
`;

const SidebarItems = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
  overflow: hidden;
`;

const SidebarItem = styled(NavLink)`
  align-items: center;
  border-radius: var(--size-border-radius-md);
  color: var(--color-components-sidenav-menu-item-fg);
  display: flex;
  gap: 0.6rem;
  height: 40px;
  padding-left: 4px;
  position: relative;
  transition: all 0.2s ease-in-out;

  ${Icon.S.Icon} svg {
    width: 28px;
    height: 28px;
  }

  &:hover {
    background-color: var(--color-components-sidenav-menu-item-bg-hover);
    border-radius: var(--size-border-radius-md);
  }

  &.active {
    background-color: var(--color-components-sidenav-menu-item-bg-active);
    color: var(--color-components-sidenav-menu-item-fg-active);
    font-weight: 700;
  }
`;

const SidebarItemIcon = styled.div`
  align-items: center;
  display: flex;
  height: 4rem;
  justify-content: center;
  min-width: 4rem;
  width: 4rem;
`;

const SidebarItemLabel = styled(Typography)`
  color: inherit;
  font-size: 14px;
`;

const SidebarBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .tooltip-container {
    font-weight: 400;
    padding: 0.4em 0.6em;
    width: max-content;
  }
`;

const Sidebar = styledClient(motion.div, 'Sidebar')`
  background-color: var(--color-components-sidenav-bg);
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  padding: 16px 20px;
  position: fixed;
  top: 0;
  transition: all 0.2s ease-in-out;
  z-index: 10;

  ${DEVICE.mobile.max} {
    border-bottom: 1px solid var(--color-components-sidenav-bg);
    flex-direction: column;
    height: fit-content;
    padding: 13px 12px;
    right: 0;
  }

  ${({ hasNavigationHeader }) =>
    hasNavigationHeader &&
    css`
      top: 41px;
    `}

  ${({ isSidebarOpen }) => css`
    width: ${isSidebarOpen ? SIDEBAR_WIDTH.open : SIDEBAR_WIDTH.closed};

    ${DEVICE.mobile.max} {
      width: ${isSidebarOpen ? '100%' : SIDEBAR_WIDTH.closed};
    }
  `}

  ${({ isSidebarOpen }) =>
    isSidebarOpen &&
    css`
      ${DEVICE.mobile.max} {
        height: unset;
      }
    `}

  ${SidebarBody} {
    ${DEVICE.mobile.max} {
      display: none;

      ${({ isSidebarOpen }) =>
        isSidebarOpen &&
        css`
          display: flex;
        `}
    }
  }

  @keyframes blink {
    from {
      opacity: 0.4;
    }
    to {
      opacity: 1;
    }
  }
`;

const SidebarBottom = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SidebarBottomItems = styled.div``;

const SidebarBottomItem = styled.button`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 16px;
  height: 48px;
  padding: 10px;

  &:hover {
    /* background-color: var(--color- */
    /* border: 1px solid var(--color- */
    border-radius: var(--size-border-radius-md);
  }
`;

const SidebarBottomItemLogo = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-width: 32px;
`;

const SidebarBottomItemLabel = styled.label`
  min-width: max-content;
`;

const SidebarHelpContentBody = styled.div`
  background-color: var(--color-components-input-bg-default);
  border: 1px solid var(--color-components-input-border-default);
  border-radius: var(--size-border-radius-md);
  padding: 8px;
`;

const SidebarAccountContentBody = styled.div`
  background-color: var(--color-components-input-bg-default);
  border: 1px solid var(--color-components-input-border-default);
  border-radius: var(--size-border-radius-md);
  padding: 8px;

  ${Badge?.S?.Badge} {
    cursor: default;
    text-transform: capitalize;
  }
`;

const SideBarAccountContent = styled.div`
  bottom: -16px;
  cursor: default;
  display: none;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  left: calc(100% - 8px);
  padding: 16px;
  position: absolute;
  width: fit-content;
`;

const AccountNameContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${Typography.S.Typography} {
    max-width: 100px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SidebarHelpContent = styled.div`
  bottom: -16px;
  cursor: default;
  display: none;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  left: calc(100% - 8px);
  padding: 16px;
  position: absolute;
  width: fit-content;
`;

const SidebarHelp = styled.div`
  align-items: center;
  border-radius: var(--size-border-radius-md);
  cursor: pointer;
  display: flex;
  gap: 4px;
  min-height: 40px;
  padding: 0 4px;
  position: relative;
  transition: all 0.2s ease-in-out;
  z-index: 0;

  &:before {
    content: '';
    background: linear-gradient(45deg, #ffffff, #0069e066, #00ffd5);
    position: absolute;
    top: -1px;
    left: -1px;
    background-size: 400%;
    z-index: -1;
    filter: blur(2px);
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    border-radius: var(--size-border-radius-md);
    animation: glowing 20s linear infinite;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:hover:after {
    z-index: -1;
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--color-components-sidenav-bg);
    left: 0;
    top: 0;
    border-radius: var(--size-border-radius-md);
  }

  &:hover {
    border-radius: var(--size-border-radius-md);
  }

  &:hover:before {
    opacity: 1;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      border-radius: var(--size-border-radius-md);

      &:after {
        z-index: -1;
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: var(--color-components-sidenav-bg);
        left: 0;
        top: 0;
        border-radius: var(--size-border-radius-md);
      }

      &:before {
        opacity: 1;
      }
    `}

  &:active {
    &:after {
      background: transparent;
    }
  }

  @keyframes glowing {
    0% {
      background-position: 0 0;
    }
    50% {
      background-position: 400% 0;
    }
    100% {
      background-position: 0 0;
    }
  }

  &.active {
    background-color: var(--color-components-sidenav-avatar-bg-active);
  }

  ${Typography.S.Typography} {
    ${({ isSidebarOpen }) =>
      isSidebarOpen &&
      css`
        color: var(--color-components-sidenav-avatar-fg-inverse);
      `}
    cursor: pointer;
    white-space: nowrap;
  }

  ${Button.S.Button} {
    color: var(--color-components-sidenav-avatar-fg);
    font-size: 14px;
  }

  ${Button.S.Button} {
    color: var(--color-fg-default);
  }

  ${SidebarHelpContent} {
    display: none;
  }

  &:hover ${SidebarHelpContent} {
    display: flex;

    ${SidebarHelpContentBody} {
      ${Typography.S.Typography} {
        color: var(--color-fg-default);
      }
    }
  }

  .question-icon {
    path {
      &:first-child {
        fill: var(--color-components-sidenav-avatar-bg);
      }
    }
  }
`;
const SideBarAccount = styled.div`
  align-items: center;
  border-radius: var(--size-border-radius-md);
  cursor: pointer;
  display: flex;
  gap: 4px;
  min-height: 40px;
  padding: 0 4px;
  position: relative;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--color-components-sidenav-avatar-bg-hover);
    border-radius: var(--size-border-radius-md);
  }

  &.active {
    background-color: var(--color-components-sidenav-avatar-bg-active);
  }

  ${Typography.S.Typography} {
    white-space: nowrap;
  }

  ${SideBarAccountContent} {
    display: none;
  }

  &:hover ${SideBarAccountContent} {
    display: flex;
  }
`;

const SideBarAccountOpenMenu = styled.div`
  display: block;
  ${Typography.S.Typography} {
    color: var(--color-components-sidenav-avatar-fg-inverse);
    max-width: 150px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const SidebarAccount = styled(NavLink)`
  align-items: center;
  border-radius: var(--size-border-radius-md);
  display: flex;
  gap: 4px;
  height: 40px;
  overflow: hidden;
  padding: 0.4rem;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: var(--color-components-sidenav-avatar-bg-hover);
    border-radius: var(--size-border-radius-md);
  }

  &.active {
    background-color: var(--color-components-sidenav-avatar-bg-active);
  }
`;

const SidebarAccountInfo = styled.div``;

const SidebarAccountInfoName = styled(Typography)`
  color: var(--color-components-sidenav-avatar-fg-inverse);
  line-height: 150%;
`;

const SidebarAccountInfoEmail = styled(Typography)`
  color: var(--color-components-sidenav-avatar-fg);
`;

const OnboardingListItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const OnboardingList = styled.div`
  /* border: 1px solid var(--color */
  border-radius: var(--size-border-radius-lg);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;

const OnboardingHotspot = styled.div`
  align-items: center;
  /* background: var(--color */
  border-radius: var(--size-border-radius-lg);
  display: flex;
  justify-content: space-between;
  margin: 16px 0;
  padding: 16px;
`;

const Onboarding = styled.li`
  position: relative;
`;

const Indicator = styled.div`
  align-items: center;
  border-radius: var(--size-border-radius-circle);
  box-shadow: var(--shadow-xl);
  display: flex;
  height: 26px;
  justify-content: center;
  position: absolute;
  right: -4px;
  top: -4px;
  width: 26px;
`;

const AccountButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;

  ${Button?.S?.Button} {
    &:last-of-type {
      svg {
        transform: rotate(90deg);
      }
    }
  }
`;

const AccountButtonLine = styled.div`
  background-color: var(--color-border-default);
  height: 1px;
  width: 100%;
`;

const SidebarHelpTooltip = styled.div`
  background-color: var(--color-bg-body);
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  padding: 8px;
`;

const DropdownBackdrop = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10;
`;

export {
  AccountButtonLine,
  AccountButtonWrapper,
  AccountNameContentWrapper,
  DropdownBackdrop,
  Indicator,
  Onboarding,
  OnboardingHotspot,
  OnboardingList,
  OnboardingListItem,
  SideBarAccount,
  SideBarAccountContent,
  SideBarAccountOpenMenu,
  Sidebar,
  SidebarAccount,
  SidebarAccountContentBody,
  SidebarAccountInfo,
  SidebarAccountInfoEmail,
  SidebarAccountInfoName,
  SidebarBody,
  SidebarBottom,
  SidebarBottomItem,
  SidebarBottomItemLabel,
  SidebarBottomItemLogo,
  SidebarBottomItems,
  SidebarButton,
  SidebarButtonIcon,
  SidebarHeader,
  SidebarHeaderButton,
  SidebarHeaderLogo,
  SidebarHeaderLogoText,
  SidebarHelp,
  SidebarHelpContent,
  SidebarHelpContentBody,
  SidebarHelpTooltip,
  SidebarItem,
  SidebarItemIcon,
  SidebarItemLabel,
  SidebarItems
};
